import { useState } from 'react';
// Навигация — ЕДИНАЯ иконная система lucide-react (founder-ревью 2026-08-20:
// прежний микс фирменных 16px-филлов, nav24-глифа со stroke-2 и lucide-хвостов
// давал разную толщину линий и смесь filled/outline в одном ряду).
// Стандарт рельса: 24px-сетка lucide, strokeWidth 2, рендер 20px (NAV_ICON);
// все глифы контурные, активное состояние = зелёная пилюля, НЕ заливка глифа.
// Фирменный сет BrandIcon остаётся на прочих поверхностях (справочники, PA).
import {
  BookOpen,
  Building2,
  CalendarRange,
  ChevronDown,
  Crown,
  FlaskConical,
  LandPlot,
  LayoutDashboard,
  LogOut,
  Settings,
  Sprout,
  Tractor,
  User,
  Users,
  Warehouse,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { AppVersionBadge } from '@/components/layout/AppVersionBadge';
import { useScopeMode } from '@/components/scope/useScopeMode';
import { useAccountSwitcher } from '@/components/profile/AccountSwitcher';
import { cn } from '@/lib/utils';
import { useConsultantProfile } from '@/lib/consultant/useConsultantProfile';
import { useActiveSeason } from '@/lib/season/SeasonContext';

export interface SidebarMe {
  user: {
    display_name?: string;
    email: string;
    first_name?: string;
    platform_role?: 'superadmin' | 'admin' | 'support' | null;
  };
  active_tenant?: {
    id: string;
  };
  memberships: Array<{
    tenant: { id: string; slug: string; name: string };
    role: string;
  }>;
}

interface SidebarProps {
  me: SidebarMe | null;
  pathname: string;
  onNavigate: (path: string) => void;
  onLogout: () => void;
  /**
   * Свёрнутый режим (только иконки, 72px). AppShell: на десктопе (≥768) —
   * всегда свёрнутый рельс с тултипами, мобильная шторка — развёрнутый 220px
   * (AK-974: авторазворот на ≥1366 из #1109 откачен, не было approve founder).
   */
  collapsed?: boolean;
  /** Открыть ProfileDrawer (AK-229). Если не передан — fallback на навигацию /settings/profile. */
  onOpenProfile?: () => void;
}

/**
 * Единый стандарт иконок навигационного рельса: lucide 24px-грид,
 * strokeWidth 2 (дефолт семейства — вес одинаков у всех глифов по построению),
 * рендер 20×20. Не переопределять точечно — разнобой весов и был багом.
 */
const NAV_ICON = { size: 20, strokeWidth: 2 } as const;

/** Иконки внутри дропдаунов (настройки, аккаунт) — тот же вес, 16×16. */
const MENU_ICON = { size: 16, strokeWidth: 2 } as const;

interface MenuItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  /** Куда ведёт пункт. */
  to: string;
  /**
   * Подсветка. По умолчанию — совпадение ПЕРВОГО сегмента адреса с `key`:
   * `/farm/<id>` подсвечивает «Данные хозяйства», `/operations/<id>` —
   * «Операции». Пункты одного раздела с разными адресами (посевной план и
   * годовой план оба под `/planning`) задают правило сами.
   */
  isActive?: (pathname: string) => boolean;
}

const firstSegment = (pathname: string) => pathname.split('/')[1] ?? '';

/** `/planning/seasons/<год>/sowing-plan` — экран «Посевной план». */
const isSowingPlanPath = (pathname: string) =>
  /^\/planning\/seasons\/[^/]+\/sowing-plan(\/|$)/.test(pathname);

// «Скоро»-механики в навигации больше НЕТ (founder 20.08.2026): в сайдбаре
// живут только работающие разделы. Гарантия AK-294 «нет мёртвой навигации»
// теперь структурная — раздел без роута в меню просто не добавляется
// (гард: nav-brand-icons.test.tsx запрещает `comingSoon` в этом файле).

/**
 * Брендовый знак ИИ-Колхоз — тот же ассет, что и в фавиконе
 * (`frontend-vite/public/favicon.svg`, отдаётся Vite по корню). Founder UAT
 * 2026-07-07: «Лого не тот, возьми тот, что в фавиконе». Vite public-asset →
 * ссылаемся по абсолютному пути, без импорта.
 */
const FAVICON_LOGO_SRC = '/favicon.svg';

/**
 * ProductBrand — слот ПРОДУКТА сверху сайдбара (AK-381 → AK-665).
 *
 * Всегда знак ИИ-Колхоза и ничего кроме. До AK-665 слот показывал логотип
 * тенанта, если он загружен: у компаний без логотипа (Кристалл, Зелюки) в нём
 * стоял бренд, а у Просторов — их круглый оранжевый знак, тот же самый, что
 * уже висит рядом с названием компании в шапке. Получалось, что один и тот же
 * логотип занимает два места подряд, а продукт в интерфейсе не назван вовсе:
 * человек видел не «ИИ-Колхоз, кабинет Просторов», а «Просторы, Просторы».
 *
 * Разделение простое и постоянное: слот продукта — наш бренд, логотип
 * компании — только у названия тенанта в шапке (`TopBar`). Поэтому у слота
 * больше нет входов от тенанта: подставить сюда чужой логотип нечем.
 */
function ProductBrand() {
  const [faviconBroken, setFaviconBroken] = useState(false);

  if (!faviconBroken) {
    return (
      <img
        src={FAVICON_LOGO_SRC}
        alt="ИИ-Колхоз"
        data-testid="sidebar-product-brand"
        onError={() => setFaviconBroken(true)}
        className="h-9 w-9 object-contain"
      />
    );
  }

  // Фолбэк на случай, если ассет не загрузился, — тоже наш бренд, буквами.
  return (
    <div
      className="flex h-9 min-w-9 items-center justify-center rounded-md bg-primary/10 px-2 text-sm font-bold text-primary"
      aria-label="ИИ-Колхоз"
      data-testid="sidebar-product-brand"
    >
      ИК
    </div>
  );
}

/**
 * Sidebar — 72px рельс на десктопе (только иконки с тултипами) / 220px
 * развёрнутый в мобильной шторке (<768).
 *
 * Верх: знак продукта + меню по частоте (AK-898). Переключателя хозяйства
 * здесь нет — он в шапке (AK-974, часть 2).
 * Низ: Platform Admin?, Настройки (⚙ Компания — админам), аккаунт, версия.
 */
export function Sidebar({
  me,
  pathname,
  onNavigate,
  onLogout,
  collapsed = true,
  onOpenProfile,
}: SidebarProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Меню под аватаром — контролируемое: список привязанных аккаунтов
  // запрашиваем только когда меню реально открыли (AK-469).
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { menuItems: accountItems, stepUpDialog } = useAccountSwitcher({
    enabled: userMenuOpen,
  });
  const isPlatformAdmin = !!me?.user.platform_role;
  // Тип компании читается из СВОЕЙ схемы (`/consultant/profile`), а не из
  // `/companies`: последняя внутри окна клиента отдала бы тип клиента, и меню
  // консультанта схлопнулось бы ровно тогда, когда из окна надо выйти.
  const { isConsultant, hasOwnFields } = useConsultantProfile();

  const activeMembershipRole = me
    ? me.active_tenant?.id
      ? me.memberships.find((m) => m.tenant.id === me.active_tenant?.id)?.role
      : me.memberships[0]?.role
    : undefined;
  // AK-350 — «Компания»: единый хаб (структура/команда/роли/справочники/
  // интеграции/профиль). Клиентский гейт по admin-роли (company_admin/
  // top_level/PA), как раньше у «Команды» и «Структуры». Финальная проверка
  // прав — на бэке (require_structure_write / _require_can_manage); при 403
  // страница покажет сообщение.
  const showCompany =
    activeMembershipRole === 'company_admin' ||
    activeMembershipRole === 'top_level' ||
    isPlatformAdmin;

  // AK-898 — меню v2 ПО ЧАСТОТЕ использования (founder 08.10, В-6: «смешаны
  // разные по частоте запроса кнопки и разделы»). Ежедневное — на виду; то,
  // что заводят один раз и иногда правят (поля, техника, люди, отделения,
  // история культур), — в одном пункте «Данные хозяйства». Режим читается из
  // области сессии (`useScopeMode`): «Холдинг» = только общая аналитика (В-2).
  //
  // Что ушло из меню хозяйства и почему адреса живы:
  //   · «На главную» (`/dashboard`) — в режиме хозяйства адрес уводит в
  //     «Данные хозяйства» (R1; AK-896 уточнит по заполненности двойника),
  //     в режиме «Холдинг» это и есть экран «Холдинг»;
  //   · «Хозяйство (подготовка)» — слилось в «Данные хозяйства» (AK-899);
  //   · «Поля» — вкладка «Данных хозяйства» и карта [умолч. 5]; `/fields` и
  //     `/fields/<id>` открываются по закладке и из карты;
  //   · «Аналитика» [умолч. 13] — `/analytics/history` по прямой ссылке и
  //     кнопкой с экрана «Холдинг».
  // «Операции» остаются до R2 [умолч. 12] — иначе с Desktop пропал бы рабочий
  // экран учёта факта.
  //
  // Семантика глифов — lucide 24/stroke 2 (стандарт рельса): founder 20.08
  // закрепил LandPlot за «Картой» («поле с флажком ок») и LayoutDashboard за
  // сводкой («это дашборд по сути») — у нас это «Холдинг»; Users — «CRM».
  const { mode: scopeMode } = useScopeMode();
  const holdingMode = scopeMode === 'holding';

  // «Посевной план» открывает сезонный экран текущего сезона сессии. Сезон
  // ещё не известен (или контекста нет) — ведём в хаб планирования, а не на
  // адрес без года: мёртвых адресов в меню не бывает.
  const { season } = useActiveSeason();
  const sowingPlanTo = season
    ? `/planning/seasons/${season.year}/sowing-plan`
    : '/planning';

  // Раздел «CRM» (нав-имя, founder 20.08.2026) — только у консалтинговой и
  // гибридной компании. Раньше его видели ВСЕ, включая чистых аграриев:
  // раздел, которого у них не бывает, стоял вторым пунктом меню. Ключ ведёт
  // на /clients (список из внешней системы продаж), а не на /crm — журнал
  // активностей открывается из карточки клиента. Users (несколько фигур);
  // одиночный User остаётся за «Профилем» в дропдауне аккаунта.
  const crmItems: MenuItem[] = isConsultant
    ? [{ key: 'clients', label: 'CRM', icon: <Users {...NAV_ICON} />, to: '/clients' }]
    : [];

  // AK-385 — «Справочники» ведёт в справочник пестицидов (СЗР). Прочие
  // reference-разделы для тенанта появятся позже; пока живой роут один.
  // BookOpen = справочник (книга). Остаётся нижним пунктом в обоих режимах —
  // агроному в сезон нужны культуры и препараты [умолч. 14].
  const referenceItem: MenuItem = {
    key: 'reference',
    label: 'Справочники',
    icon: <BookOpen {...NAV_ICON} />,
    to: '/reference/pesticides',
  };

  const mainItems: MenuItem[] = holdingMode
    ? [
        ...crmItems,
        // Режим «Холдинг» (макет v2 экран 4): нынешняя главная — счётчики,
        // структура культур, севооборот; таблица хозяйств — AK-903.
        { key: 'dashboard', label: 'Холдинг', icon: <LayoutDashboard {...NAV_ICON} />, to: '/dashboard' },
        referenceItem,
      ]
    : [
        ...crmItems,
        { key: 'map', label: 'Карта', icon: <LandPlot {...NAV_ICON} />, to: '/map' },
        { key: 'operations', label: 'Операции', icon: <Tractor {...NAV_ICON} />, to: '/operations' },
        // AK-938 — «Наука»: опыты на полях и схемы опытов (founder 21.10, low-fi
        // PR #1177: пункт рельса между «Операциями» и планированием, колба).
        { key: 'science', label: 'Наука', icon: <FlaskConical {...NAV_ICON} />, to: '/science' },
        {
          key: 'sowing-plan',
          label: 'Посевной план',
          icon: <Sprout {...NAV_ICON} />,
          to: sowingPlanTo,
          isActive: isSowingPlanPath,
        },
        // AK-434 — годовое планирование (хаб `/planning`): всё под `/planning`,
        // кроме посевного плана — у того свой пункт выше.
        {
          key: 'planning',
          label: 'Годовой план',
          icon: <CalendarRange {...NAV_ICON} />,
          to: '/planning',
          isActive: (p) => firstSegment(p) === 'planning' && !isSowingPlanPath(p),
        },
        // AK-899 — «Данные хозяйства»: цифровой двойник выбранного хозяйства
        // (поля, отделения, техника, люди, история культур). `/farm` → редирект
        // в `/farm/<id>` по области; подсветка — по первому сегменту `farm`.
        // У чистого консультанта своих хозяйств не бывает — пункта нет.
        ...(hasOwnFields
          ? [
              {
                key: 'farm',
                label: 'Данные хозяйства',
                icon: <Warehouse {...NAV_ICON} />,
                to: '/farm',
              },
            ]
          : []),
        referenceItem,
      ];

  const bottomItems: MenuItem[] = [
    // AK-381 — «Начать работу» (getting-started) убран из сайдбара. Роут
    // /getting-started остаётся (см. router.tsx), только вход из меню удалён.
    ...(isPlatformAdmin
      ? [
          // Крона PA — тот же вес и цвет, что весь ряд (founder-ревью
          // 2026-08-20: «последняя иконка другим цветом» — янтарный
          // text-accent-brand убран, глиф Crown сам по себе различим).
          {
            key: 'platform-admin',
            label: 'Platform Admin',
            icon: <Crown {...NAV_ICON} />,
            to: '/platform-admin',
          },
        ]
      : []),
  ];

  // Раздел «Настройки» (под шестерёнкой).
  //
  // AK-381: «Компания» (company hub, AK-350) переехала из основного меню сюда —
  // остаётся доступной admin-ролям, но живёт в настройках, а не в топ-навигации.
  // AK-350: «Структура»/«Команда» ранее слиты в «Компания» — здесь их дублей нет.
  //
  // Иконки заданы в 16px (MENU_ICON): эти пункты рендерятся только внутри
  // меню настроек (collapsed-dropdown), в 20px-рельсе они не показываются —
  // см. комментарий у блока «Settings» ниже.
  // «Тех. операции» («скоро») убраны founder-решением 20.08.2026 — вернуть
  // пункт, когда домен зальёт роут. Глиф при возврате — НЕ шестерёнка
  // (занята разделом «Настройки», урок #525/AK-463), брать SlidersHorizontal.
  const settingsChildren: MenuItem[] = [
    // Реальный роут /company (router.tsx), гейт по admin-роли.
    ...(showCompany
      ? [
          {
            key: 'company',
            label: 'Компания',
            icon: <Building2 {...MENU_ICON} />,
            to: '/company',
          },
        ]
      : []),
  ];
  // Нет живых пунктов настроек (не-админ) — шестерёнку не показываем вовсе:
  // пустое меню = та же заглушка, а заглушек в навигации не бывает.
  const hasSettingsChildren = settingsChildren.length > 0;

  /**
   * Открыт какой-либо раздел настроек — подсвечиваем шестерёнку в рельсе.
   * Сейчас это /company (company hub, AK-350) и будущие /settings/*.
   */
  const settingsSectionActive =
    pathname === '/company' ||
    pathname.startsWith('/company/') ||
    pathname === '/settings' ||
    pathname.startsWith('/settings/');

  const isActive = (item: MenuItem) =>
    item.isActive ? item.isActive(pathname) : firstSegment(pathname) === item.key;

  const renderItem = (item: MenuItem) => {
    const active = isActive(item);
    const handleClick = () => onNavigate(item.to);

    if (collapsed) {
      return (
        <Tooltip key={item.key} delayDuration={150}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleClick}
              className={cn(
                // w-auto + mx-auto = по центру, ширина по icon-size + h-10 квадрат
                'mx-auto my-1 flex h-10 w-10 items-center justify-center rounded-md transition-colors',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-accent',
              )}
              aria-label={item.label}
            >
              <span
                className={cn(
                  '[&>svg]:h-5 [&>svg]:w-5',
                  active && 'text-primary-foreground',
                )}
              >
                {item.icon}
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{item.label}</TooltipContent>
        </Tooltip>
      );
    }

    return (
      <button
        key={item.key}
        type="button"
        onClick={handleClick}
        className={cn(
          'flex h-10 w-full items-center gap-3 px-4 transition-colors text-sm',
          active
            ? 'bg-primary text-primary-foreground'
            : 'text-foreground hover:bg-accent',
        )}
      >
        <span
          className={cn(
            '[&>svg]:h-5 [&>svg]:w-5 shrink-0',
            active && 'text-primary-foreground',
          )}
        >
          {item.icon}
        </span>
        <span className="truncate">{item.label}</span>
      </button>
    );
  };

  const displayName =
    me?.user.display_name ?? me?.user.first_name ?? me?.user.email ?? '...';
  const avatarLetter = displayName[0]?.toUpperCase() ?? '?';

  return (
    <div
      className={cn(
        'flex h-full flex-col bg-card border-r border-border',
        collapsed ? 'w-[72px]' : 'w-[220px]',
      )}
    >
      {/* Top: logo + main menu */}
      <div className="flex-1 overflow-y-auto">
        <button
          type="button"
          onClick={() => onNavigate('/dashboard')}
          title="На главную"
          className="flex h-14 w-full items-center justify-center border-b border-border px-2 text-primary hover:bg-accent transition-colors"
        >
          <ProductBrand />
        </button>
        {/* Переключателя хозяйства в сайдбаре НЕТ — ни в рельсе, ни в мобильной
            шторке (AK-974, часть 2; approve founder 20.10, комменты 18132/
            18140/18141). На проде v1.2010 один контрол выбора: на десктопе —
            пилюля в шапке, на мобайле — `FarmSwitcher header` в шапке.
            Гард: `__tests__/Sidebar.test.tsx` «переключателя хозяйства нет». */}
        <nav className="py-2">{mainItems.map(renderItem)}</nav>
      </div>

      {/* Bottom: settings + user */}
      <div className="border-t border-border py-2">
        {bottomItems.map(renderItem)}

        {/* Settings — collapsed: ОДНА живая шестерёнка, по клику меню с теми же
            пунктами, что и в expanded (founder UAT 2026-07-28 по скрину:
            «Зачем тут 2 иконки?»). Заглушек нет ни в рельсе, ни в меню
            (founder 20.08.2026): если живых пунктов настроек у роли нет —
            шестерёнка не рендерится вовсе.
            Expanded: dropdown chevron с sub-items. */}
        {hasSettingsChildren &&
          (collapsed ? (
            <DropdownMenu>
              <Tooltip delayDuration={150}>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label="Настройки"
                      className={cn(
                        'mx-auto my-1 flex h-10 w-10 items-center justify-center rounded-md transition-colors',
                        settingsSectionActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground hover:bg-accent',
                      )}
                    >
                      <Settings {...NAV_ICON} />
                    </button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="right">Настройки</TooltipContent>
              </Tooltip>
              <DropdownMenuContent side="right" align="end" className="w-52">
                {settingsChildren.map((c) => (
                  <DropdownMenuItem key={c.key} onSelect={() => onNavigate(c.to)}>
                    {c.icon}
                    <span className="truncate">{c.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div>
              <button
                type="button"
                onClick={() => setSettingsOpen((v) => !v)}
                className={cn(
                  'flex h-10 w-full items-center justify-between px-4 text-sm hover:bg-accent transition-colors',
                  settingsSectionActive && 'bg-primary/10 text-primary',
                )}
              >
                <span className="flex items-center gap-3">
                  <Settings {...NAV_ICON} />
                  <span>Настройки</span>
                </span>
                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 transition-transform',
                    settingsOpen && 'rotate-180',
                  )}
                />
              </button>
              {settingsOpen && (
                <div className="bg-secondary/30">
                  {settingsChildren.map((c) => {
                    const active = firstSegment(pathname) === c.key;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => onNavigate(c.to)}
                        className={cn(
                          'flex h-9 w-full items-center gap-3 pl-12 pr-4 text-sm transition-colors hover:bg-accent',
                          active && 'bg-primary/10 text-primary',
                        )}
                      >
                        <span className="truncate">{c.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

        {/* User dropdown (avatar + name + chevron) */}
        <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex w-full items-center gap-2 border-t border-border px-3 py-3 hover:bg-accent transition-colors text-sm',
                collapsed && 'justify-center px-0',
              )}
            >
              <Avatar className="h-7 w-7 shrink-0">
                <AvatarFallback>{avatarLetter}</AvatarFallback>
              </Avatar>
              {!collapsed && (
                <>
                  <span className="flex-1 truncate text-left font-medium">
                    {displayName}
                  </span>
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          {/* w-72 вместо w-48: в строке аккаунта две строки текста (имя +
              почта) плюс метка справа — в 192px всё схлопывалось в многоточие.
              max-w по вьюпорту — чтобы меню не вылезало на узких экранах. */}
          <DropdownMenuContent
            align={collapsed ? 'start' : 'end'}
            side="top"
            /* w-80: имя и почта аккаунта должны читаться целиком, на 288px
               они обрезались в многоточие (founder 2026-07-29). */
            className="w-80 max-w-[calc(100vw-2rem)]"
          >
            {/* AK-469 — список аккаунтов + «Добавить аккаунт» + разделитель.
                Существующие пункты ниже не менялись. */}
            {accountItems}
            <DropdownMenuItem
              onSelect={() =>
                onOpenProfile ? onOpenProfile() : onNavigate('/settings/profile')
              }
            >
              <User {...MENU_ICON} />
              Профиль
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onLogout}>
              <LogOut {...MENU_ICON} />
              Выйти
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Диалог подтверждения кодом — СНАРУЖИ меню: Radix размонтирует
            содержимое DropdownMenuContent при закрытии, а меню закрывается
            ровно в момент выбора аккаунта (AK-469). */}
        {stepUpDialog}

        {/* Версия приложения (AK-274) — ненавязчиво в самом низу. */}
        <div className="border-t border-border pt-1">
          <AppVersionBadge collapsed={collapsed} />
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
